import { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { Home, LayoutGrid, GitMerge, CheckCircle2, Search, Settings,  FolderGit2, TerminalSquare, FlaskConical, Beaker, ClipboardEdit, Globe, BarChart, Shuffle } from "lucide-react";
import { useRepoStore } from "@/stores/repo";
import { useProjectsStore } from "@/stores/projects";
import { useUiStore } from "@/stores/ui";
import useSyncStore from "@/stores/sync";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useWorkspaceStore } from "@/stores/workspace";
import { useIsTablet } from "@/hooks/use-is-tablet";

import { useGlobalShortcuts } from "@/hooks/useGlobalShortcuts";
import notificationsManager from "@/lib/notifications-ws";
import { GlobalPasteHandler } from "./GlobalPasteHandler";
const navItems = [
    { to: "/pages/projects/", icon: GitMerge, label: "Projects" },
    { to: "/pages/workspace/", icon: Home, label: "Workspace" },
    { to: "/pages/tasks/", icon: LayoutGrid, label: "Tasks" },
    { to: "/pages/review/", icon: CheckCircle2, label: "Review", requiresReviewTask: true },
    { to: "/pages/dev-session/", icon: TerminalSquare, label: "Dev Session" },
    { to: "/pages/tests/", icon: FlaskConical, label: "Tests" },
    { to: "/pages/circular-dependencies/", icon: Shuffle, label: "Cycles" },
    { to: "/pages/proxy/", icon: Globe, label: "App Proxy", requiresProxy: true },
    // { to: "/pages/context-fetch-test/", icon: Beaker, label: "Context Test" },
    { to: "/pages/usage-stats/", icon: BarChart, label: "Usage" },
    { to: "/pages/settings/", icon: Settings, label: "Settings" },
];

const bottomNavItems = [
    { to: "/pages/projects/", icon: GitMerge, label: "Projects" },
    { to: "/pages/workspace/", icon: Home, label: "Workspace" },
    { to: "/pages/tasks/", icon: LayoutGrid, label: "Tasks" },
    { to: "/pages/review/", icon: CheckCircle2, label: "Review", requiresReviewTask: true },
    { to: "/pages/settings/", icon: Settings, label: "Settings" },
];

function BottomNav({ isVisible, showNav }: { isVisible: boolean, showNav: () => void }) {
  const isMobile = useIsMobile();
  const reviewTaskId = useUiStore((state) => state.reviewTaskId);

  if (!isMobile) {
    return null;
  }

  if (!isVisible) {
    return (
      <div className="fixed bottom-0 left-0 right-0 h-8 flex justify-center items-center z-20 md:hidden">
        <button
          onClick={showNav}
          className="w-12 h-2 bg-muted-foreground rounded-full hover:bg-primary transition-colors"
          aria-label="Show navigation"
        />
      </div>
    );
  }

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 border-t bg-background z-20 md:hidden"
      onPointerEnter={showNav}
    >
      <div className="flex h-16 items-center px-2 gap-1">
        {bottomNavItems.map((item) => {
          if (item.requiresReviewTask && !reviewTaskId) {
            return null;
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `flex flex-1 min-w-0 flex-col items-center gap-1 p-1 rounded-md text-xs transition-colors ${isActive ? "text-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}
            >
              <item.icon className="h-5 w-5" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}

const FullscreenPrompt = ({ onEnter }: { onEnter: () => void }) => (
    <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-sm flex items-center justify-center p-4">
        <Card className="max-w-sm">
            <CardHeader>
                <CardTitle>Full Screen Required</CardTitle>
                <CardDescription>
                    For the best experience, this application requires full screen mode on mobile devices.
                </CardDescription>
            </CardHeader>
            <CardFooter>
                <Button className="w-full" onClick={onEnter}>
                    Enter Full Screen
                </Button>
            </CardFooter>
        </Card>
    </div>
);

export function AppLayout() {
  const location = useLocation();
  const repoState = useRepoStore();
  const activeProjectId = useProjectsStore(state => state.activeProjectId) ?? 'NO_PROJECT';
  const activeProjectProxyPort = useProjectsStore(state => 
    state.activeProjectId ? state.byId[state.activeProjectId]?.proxyPort : undefined
  );
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    headerTitle,
    reviewTaskId,
    isGitPanelVisible,
    isFileExplorerSearchVisible,
    setIsCommandPaletteOpen,
    setIsFileExplorerSearchVisible,
    setIsGitPanelVisible,
  } = useUiStore();
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(false);
  const [isBottomNavVisible, setIsBottomNavVisible] = useState(true);
  const bottomNavTimeoutRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const [isSidebarHovered, setIsSidebarHovered] = useState(false); 
  const activeProjectServerPath = useProjectsStore(state => state.activeProjectId ? state.byId[state.activeProjectId]?.serverPath : null);
  const triggerPaste = useUiStore((state) => state.triggerPaste)
  const createNewFile = useWorkspaceStore(state => state.createNewFile)

  useEffect(() => {
    const handleGlobalPaste = (event: ClipboardEvent) => {
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (document.activeElement?.closest('.monaco-editor')) {
        return
      }
      triggerPaste(event.clipboardData?.getData('text') ?? '')
    }
    window.addEventListener('paste', handleGlobalPaste, false);

    return () => {
      window.removeEventListener('paste', handleGlobalPaste)
    }
  }, [triggerPaste])

  useEffect(() => {
    if (activeProjectServerPath) {
      notificationsManager.connect();
    }
    return () => {
      notificationsManager.disconnect();
    };
  }, [activeProjectServerPath]);

  const showBottomNav = useCallback(() => {
    if (bottomNavTimeoutRef.current) {
      clearTimeout(bottomNavTimeoutRef.current);
    }
    setIsBottomNavVisible(true);

    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    if (isMobile && isPWA) {
      return;
    }

    bottomNavTimeoutRef.current = window.setTimeout(() => {
      setIsBottomNavVisible(false);
    }, 2000);
  }, [isMobile]);

  // This effect hook handles applying deferred state updates when navigating away from the workspace.
  // Using useLayoutEffect to run it synchronously after DOM mutations but before the browser paints.
  // This can help prevent flickering if the state update causes a visible change.
  const { applyPendingUpdates, status: syncStatus } = useSyncStore();
  useLayoutEffect(() => {
    const isWorkspace = location.pathname.startsWith('/pages/workspace/');
    if (!isWorkspace && syncStatus === 'stale') {
      applyPendingUpdates();
    }
  }, [location.pathname, applyPendingUpdates, syncStatus]);

  useEffect(() => {
    const handleNavigation = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail === 'string') {
        navigate(detail);
      }
    };
    window.addEventListener('navigate-to', handleNavigation);
    return () => {
      window.removeEventListener('navigate-to', handleNavigation);
    };
  }, [navigate]);

  useEffect(() => {
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;

    if (isMobile && !isPWA && !window.location.href.includes('localhost')) {
      setShowFullscreenPrompt(!document.fullscreenElement);
    } else {
      setShowFullscreenPrompt(false);
    }

    const handleFullScreenChange = () => {
      const isPWA = window.matchMedia('(display-mode: standalone)').matches;
      if (isMobile && !isPWA) {
        setShowFullscreenPrompt(!document.fullscreenElement);
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [isMobile]);

    useGlobalShortcuts();

  useEffect(() => {
    if (isMobile) {
      showBottomNav();
    } else {
      setIsBottomNavVisible(false); // Hide on desktop
    }

    return () => {
      if (bottomNavTimeoutRef.current) clearTimeout(bottomNavTimeoutRef.current);
    };
  }, [isMobile, location.pathname, showBottomNav]);

  const isReviewPage = location.pathname.startsWith('/pages/review/');

  if (isMobile && isReviewPage) {
    return (
      <div className="h-screen w-full overflow-hidden bg-background">
        <GlobalPasteHandler />
        <Outlet />
        <Toaster />
      </div>
    );
  }

  return (
    <SidebarProvider open={isMobile ? undefined : isSidebarHovered} onOpenChange={isMobile ? undefined : setIsSidebarHovered}>
        <GlobalPasteHandler />
      {isMobile && showFullscreenPrompt && (
        <FullscreenPrompt
          onEnter={() => {
            document.documentElement.requestFullscreen().catch(err => {
              console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
            });
          }}
        />
      )}
      <div className="flex h-screen w-full" key={activeProjectId}>
        <Sidebar
          onMouseLeave={() => !isMobile && setIsSidebarHovered(false)}
          collapsible={isMobile ? "offcanvas" : isSidebarHovered ? "none" : "icon"}
          className="border-r"
        >
          <SidebarHeader className="p-2" >
            <Link to="/" className="flex items-center gap-2 p-2 font-semibold" >
                  <FolderGit2 className="h-7 w-7 text-primary" />
              <span className="group-data-[state=expanded]:inline group-data-[state=collapsed]:hidden font-bold">Sprocket</span>
              </Link>
          </SidebarHeader>
          <SidebarContent>
            <SidebarMenu className="gap-1" >
              {navItems.map((item) => {
                if (item.requiresReviewTask && !reviewTaskId) {
                  return null;
                }
                if (item.requiresProxy && !activeProjectProxyPort) {
                    return null;
                }
                return (
                  <SidebarMenuItem key={item.to} onMouseEnter={() => !isMobile && setIsSidebarHovered(true)}>
                    <SidebarMenuButton asChild isActive={location.pathname.startsWith(item.to)} tooltip={{ children: item.label }} >
                      <Link to={item.to}>
                        <item.icon className="h-5 w-5" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarContent>
        </Sidebar>
        <SidebarInset className="flex-1 flex flex-col min-h-0">
          {isMobile &&     <header className="flex h-14 items-center gap-4 border-b bg-background px-4 sm:px-6 shrink-0">

              <SidebarTrigger />
              <div className="flex-1 flex items-center gap-2 overflow-hidden">
                {repoState.connectionStatus === 'connected' && repoState.name ? (
                  <>
                    <Badge variant="secondary" className="truncate">{repoState.provider}/{repoState.org}/{repoState.name}</Badge>
                    <Badge variant="outline">{repoState.branch}</Badge>
                  </>
                ) : (
                  <h1 className="text-lg font-semibold">{headerTitle || 'Sprocket'}</h1>
                )}
              </div>
          </header>}
          <div className={`flex-1 min-h-0 overflow-y-auto w-full max-w-[100vw] ${!isMobile && (isTablet || !isSidebarHovered) ? 'pl-20' : ''} ${isSidebarHovered ? 'pl-20' : ''} ${isMobile ? (isBottomNavVisible ? 'pb-20' : 'pb-8') : ''}`}>
            <Outlet />
          </div>
        </SidebarInset>
      </div>
      <Toaster />
      <BottomNav isVisible={isBottomNavVisible} showNav={showBottomNav} />
    </SidebarProvider>
  );
}